import React, { useMemo } from "react";
import {
  Box,
  Stack,
  Typography,
  Button,
  Select,
  MenuItem,
  TextField,
  Tooltip,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import LocalFireDepartmentRoundedIcon from "@mui/icons-material/LocalFireDepartmentRounded";
import AdjustRoundedIcon from "@mui/icons-material/AdjustRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import { microLabelSx } from "../../utils/fdcTheme";

const FUZE_OPTIONS = [
  { value: "PD", label: "PD" },
  { value: "TIME", label: "Time" },
];

const PROJECTILE_OPTIONS = [
  { value: "HE", label: "HE" },
  { value: "SMOKE", label: "Smoke" },
  { value: "ILL", label: "Illum" },
];

// Smoke and Illuminating are fired with a Time (mechanical) fuze.
const PROJECTILES_REQUIRING_TIME = new Set(["SMOKE", "ILL"]);

const ANGLE_OPTIONS = [
  { value: "la", label: "Low", title: "Low angle (1st arc) — flatter trajectory" },
  { value: "ha", label: "High", title: "High angle / vertical fire (2nd arc) — steep trajectory" },
];

// Volume of fire options (rounds per gun).
const ROUNDS_OPTIONS = Array.from({ length: 20 }, (_, i) => i + 1);

// Mission phase options.
const PHASE_OPTIONS = [
  { value: "AF", label: "AF", title: "Adjust Fire — only the regulating gun fires" },
  { value: "FFE", label: "FFE", title: "Fire For Effect — all guns on the mission fire" },
];

function Field({ label, children, minWidth, hideLabel = false, sx }) {
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.4, minWidth, ...sx }}>
      {!hideLabel && (
        <Typography variant="caption" sx={{ ...microLabelSx, color: "text.secondary" }}>
          {label}
        </Typography>
      )}
      {children}
    </Box>
  );
}

function GunChip({ gun, regulatingGunId, onToggle, onFocus }) {
  const theme = useTheme();
  const selected = !!gun.is_battery_center;
  const onMission = !!gun.is_on_mission;
  const hasPosition = Boolean(gun.hasPosition ?? gun.updated_at ?? gun.mgrs);
  const isReg = regulatingGunId === gun.id;
  const isPending = selected && !onMission; // selected but not yet committed

  let stateLabel;
  if (!hasPosition) stateLabel = "no position";
  else if (isPending) stateLabel = "pending — submit to mission";
  else if (selected && onMission) stateLabel = "on mission · firing next";
  else if (onMission) stateLabel = "on mission";
  else stateLabel = "available — tap to add";

  const tooltipText = `Gun ${gun.gun_number}${isReg ? " (REG)" : ""} — ${stateLabel}`;

  return (
    <Tooltip arrow placement="top" title={tooltipText}>
      <Box
        onClick={() => {
          if (!hasPosition) return;
          onToggle(gun.id);
          if (onFocus) onFocus(gun.id);
        }}
        role="button"
        tabIndex={hasPosition ? 0 : -1}
        onKeyDown={(e) => {
          if (!hasPosition) return;
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onToggle(gun.id);
            if (onFocus) onFocus(gun.id);
          }
        }}
        sx={(t) => {
          // Visual state matrix:
          //   off          : plain outline
          //   on mission   : success-tinted outline + bg (clear "registered" signal)
          //   selected+on  : yellow filled (firing next)
          //   pending      : dashed yellow border (selected, NOT yet on mission)
          //   no position  : disabled
          let borderColor = alpha(t.palette.divider, 0.95);
          let borderStyle = "solid";
          let bgcolor = "transparent";
          let color = t.palette.text.primary;

          if (!hasPosition) {
            borderColor = alpha(t.palette.divider, 0.5);
            color = t.palette.text.disabled;
          } else if (isPending) {
            borderColor = t.palette.warning.main;
            borderStyle = "dashed";
            bgcolor = alpha(t.palette.warning.main, t.palette.mode === "dark" ? 0.08 : 0.04);
            color = t.palette.warning.main;
          } else if (selected && onMission) {
            borderColor = t.palette.warning.main;
            bgcolor = alpha(t.palette.warning.main, t.palette.mode === "dark" ? 0.22 : 0.14);
            color = t.palette.warning.main;
          } else if (onMission) {
            borderColor = alpha(t.palette.success.main, t.palette.mode === "dark" ? 0.55 : 0.4);
            bgcolor = alpha(t.palette.success.main, t.palette.mode === "dark" ? 0.1 : 0.06);
            color = t.palette.text.primary;
          }

          return {
            position: "relative",
            minWidth: 44,
            height: 44,
            px: 1,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            borderRadius: 1.25,
            border: `1.5px ${borderStyle}`,
            borderColor,
            bgcolor,
            color,
            cursor: hasPosition ? "pointer" : "not-allowed",
            opacity: hasPosition ? 1 : 0.45,
            transition: "all 0.15s ease",
            "&:hover": hasPosition ? {
              borderColor: t.palette.warning.main,
            } : {},
            "&:focus-visible": {
              outline: "2px solid",
              outlineColor: t.palette.warning.main,
              outlineOffset: 2,
            },
          };
        }}
      >
        <Typography
          sx={{
            fontWeight: 700,
            fontSize: 15,
            fontVariantNumeric: "tabular-nums",
            lineHeight: 1,
          }}
        >
          {gun.gun_number}
        </Typography>
        {isReg && (
          <Typography
            sx={{
              position: "absolute",
              top: 1,
              right: 3,
              fontSize: 7.5,
              fontWeight: 600,
              letterSpacing: 0.4,
              color: "warning.main",
              lineHeight: 1,
            }}
          >
            REG
          </Typography>
        )}
        {/* On-mission status dot in bottom-right corner */}
        {onMission && (
          <Box
            sx={(t) => ({
              position: "absolute",
              bottom: 3,
              right: 3,
              width: 6,
              height: 6,
              borderRadius: "50%",
              bgcolor: t.palette.success.main,
              boxShadow: `0 0 0 1.5px ${t.palette.background.paper}`,
            })}
          />
        )}
      </Box>
    </Tooltip>
  );
}

export default function FireControlBar({
  hasMission,
  weapon,
  charge,
  projectile,
  onProjectileChange,
  projectileOptions,
  angle = "la",
  onAngleChange,
  fuzeType,
  onFuzeTypeChange,
  heightOfBurst,
  onHeightOfBurstChange,
  rounds,
  onRoundsChange,
  phase = "AF",
  onPhaseChange,
  selectedGunsCount,
  onExecuteFire,
  ptlMissing,
  disabled,
  activeObservationId,
  activeObservationShotId,
  onClearActiveObservation,
  // Gun selection props
  batteryOptions = [],
  committedGunNumbers = [],
  toggleGunForMission,
  onFocusGun,
  regulatingGunId,
  onRegulatingGunChange,
  handleAssignToMission,
  assigningGuns = false,
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const canFire = hasMission && !ptlMissing && selectedGunsCount > 0 && !disabled;
  const adjustMode = !!activeObservationId;

  const tooltip = !hasMission
    ? "Select a mission first"
    : ptlMissing
      ? "PTL is required"
      : selectedGunsCount === 0
        ? "Select at least one gun"
        : adjustMode
          ? `Apply Obs #${activeObservationShotId ?? activeObservationId} correction and fire ${selectedGunsCount} gun${selectedGunsCount === 1 ? "" : "s"}`
          : `Fire ${selectedGunsCount} gun${selectedGunsCount === 1 ? "" : "s"}`;

  const loadoutText = [weapon, charge].filter(Boolean).join(" · ") || "—";

  // Pending guns = guns the operator has selected but that aren't on the mission yet.
  // Submit button only appears for additive submissions; deselecting an on-mission gun
  // does not trigger it (the gun stays on the mission, it just won't fire next).
  const selectedGunNumbers = useMemo(() => {
    return batteryOptions
      .filter((g) => g.is_battery_center && Boolean(g.hasPosition ?? g.updated_at ?? g.mgrs))
      .map((g) => String(g.gun_number));
  }, [batteryOptions]);

  const committedSet = useMemo(() => new Set(committedGunNumbers.map(String)), [committedGunNumbers]);
  const pendingNumbers = useMemo(
    () => selectedGunNumbers.filter((n) => !committedSet.has(n)),
    [selectedGunNumbers, committedSet],
  );
  const needsSync = pendingNumbers.length > 0;

  return (
    <Box
      sx={(t) => ({
        borderRadius: 1.5,
        border: "1px solid",
        borderColor: adjustMode
          ? alpha(t.palette.warning.main, t.palette.mode === "dark" ? 0.5 : 0.35)
          : alpha(t.palette.divider, 0.95),
        bgcolor:
          t.palette.mode === "dark"
            ? alpha(t.palette.background.paper, 0.97)
            : alpha(t.palette.background.paper, 0.99),
        boxShadow:
          t.palette.mode === "dark"
            ? `0 4px 12px ${alpha(t.palette.common.black, 0.3)}`
            : `0 4px 14px ${alpha(t.palette.common.black, 0.06)}`,
        opacity: hasMission ? 1 : 0.55,
        transition: "opacity 0.2s ease, border-color 0.2s ease",
        px: 1,
        py: 0.75,
      })}
    >
      <Stack spacing={isMobile ? 0.6 : 0.75}>
        {/* Adjust mode pill (compact, only when adjusting) */}
        {adjustMode && (
          <Box
            sx={(t) => ({
              display: "inline-flex",
              alignSelf: "flex-start",
              alignItems: "center",
              gap: 0.4,
              px: 0.9,
              py: 0.3,
              borderRadius: 0.9,
              border: "1px solid",
              borderColor: alpha(t.palette.warning.main, 0.5),
              bgcolor: alpha(t.palette.warning.main, t.palette.mode === "dark" ? 0.16 : 0.1),
              color: t.palette.warning.main,
              ...microLabelSx,
              fontSize: 10,
            })}
          >
            <AdjustRoundedIcon sx={{ fontSize: 12 }} />
            {`Adjust Obs #${activeObservationShotId ?? activeObservationId}`}
            {onClearActiveObservation && (
              <IconButton
                size="small"
                onClick={onClearActiveObservation}
                sx={{ p: 0.1, ml: 0.2, color: "inherit" }}
              >
                <CloseRoundedIcon sx={{ fontSize: 12 }} />
              </IconButton>
            )}
          </Box>
        )}

        {/* Phase + Reg Gun row — both steer the next fire. */}
        <Box
          sx={(t) => ({
            display: "flex",
            alignItems: "center",
            gap: 1,
            flexWrap: "wrap",
            justifyContent: "space-between",
            pb: 0.4,
            borderBottom: "1px dashed",
            borderBottomColor: alpha(t.palette.divider, 0.7),
          })}
        >
          <Stack direction="row" spacing={0.75} alignItems="center">
            <Typography sx={{ ...microLabelSx, color: "text.secondary" }}>
              Phase
            </Typography>
            <ToggleButtonGroup
              exclusive
              value={phase}
              onChange={(_e, val) => { if (val && onPhaseChange) onPhaseChange(val); }}
              size="small"
              disabled={!hasMission}
              sx={(t) => ({
                "& .MuiToggleButton-root": {
                  border: "1px solid",
                  borderColor: alpha(t.palette.divider, 0.95),
                  borderRadius: 1,
                  px: 1.4,
                  py: 0.45,
                  minHeight: 32,
                  textTransform: "none",
                  fontWeight: 700,
                  fontSize: 12,
                  letterSpacing: 0.6,
                  "&.Mui-selected": {
                    bgcolor: alpha(t.palette.warning.main, t.palette.mode === "dark" ? 0.24 : 0.16),
                    borderColor: alpha(t.palette.warning.main, t.palette.mode === "dark" ? 0.55 : 0.35),
                    color: t.palette.warning.main,
                    "&:hover": {
                      bgcolor: alpha(t.palette.warning.main, t.palette.mode === "dark" ? 0.32 : 0.22),
                    },
                  },
                },
                "& .MuiToggleButton-root + .MuiToggleButton-root": { ml: 0.4 },
              })}
            >
              {PHASE_OPTIONS.map((p) => (
                <Tooltip key={p.value} arrow placement="top" title={p.title}>
                  <ToggleButton value={p.value}>{p.label}</ToggleButton>
                </Tooltip>
              ))}
            </ToggleButtonGroup>
            <Typography sx={{ ...microLabelSx, color: "text.secondary", ml: 0.75 }}>
              Angle
            </Typography>
            <ToggleButtonGroup
              exclusive
              value={angle}
              onChange={(_e, val) => { if (val && onAngleChange) onAngleChange(val); }}
              size="small"
              disabled={!hasMission}
              sx={(t) => ({
                "& .MuiToggleButton-root": {
                  border: "1px solid",
                  borderColor: alpha(t.palette.divider, 0.95),
                  borderRadius: 1,
                  px: 1.2,
                  py: 0.45,
                  minHeight: 32,
                  textTransform: "none",
                  fontWeight: 700,
                  fontSize: 12,
                  letterSpacing: 0.4,
                  "&.Mui-selected": {
                    bgcolor: alpha(t.palette.info?.main || "#3b82f6", t.palette.mode === "dark" ? 0.24 : 0.16),
                    borderColor: alpha(t.palette.info?.main || "#3b82f6", t.palette.mode === "dark" ? 0.55 : 0.35),
                    color: t.palette.info?.main || "#3b82f6",
                  },
                },
                "& .MuiToggleButton-root + .MuiToggleButton-root": { ml: 0.4 },
              })}
            >
              {ANGLE_OPTIONS.map((a) => (
                <Tooltip key={a.value} arrow placement="top" title={a.title}>
                  <ToggleButton value={a.value}>{a.label}</ToggleButton>
                </Tooltip>
              ))}
            </ToggleButtonGroup>
          </Stack>

          {/* Regulating gun selector — choose which gun is the reg for this mission.
              Only guns committed to the mission can be picked (others have no role here). */}
          <Stack direction="row" spacing={0.75} alignItems="center">
            <Typography sx={{ ...microLabelSx, color: "text.secondary" }}>
              Reg Gun
            </Typography>
            {(() => {
              const regCandidates = batteryOptions.filter((g) => g.is_on_mission);
              const fallback = regCandidates.length ? regCandidates : batteryOptions;
              const validIds = new Set(fallback.map((g) => g.id));
              const value = regulatingGunId && validIds.has(regulatingGunId) ? regulatingGunId : "";
              return (
                <Select
                  value={value}
                  onChange={(e) => onRegulatingGunChange && onRegulatingGunChange(e.target.value)}
                  size="small"
                  disabled={!hasMission || fallback.length === 0}
                  displayEmpty
                  renderValue={(v) => {
                    if (!v) return <span style={{ color: "rgba(0,0,0,0.4)" }}>—</span>;
                    const g = fallback.find((x) => x.id === v);
                    return g ? `Gun ${g.gun_number}` : "—";
                  }}
                  sx={{
                    height: 32,
                    minWidth: 88,
                    borderRadius: 1,
                    fontWeight: 700,
                    fontSize: 12,
                    letterSpacing: 0.4,
                    "& .MuiSelect-select": { py: 0.4, px: 1 },
                  }}
                >
                  {fallback.map((g) => (
                    <MenuItem key={g.id} value={g.id} sx={{ fontWeight: 700, fontSize: 13 }}>
                      Gun {g.gun_number}{!g.is_on_mission ? " (not on mission)" : ""}
                    </MenuItem>
                  ))}
                </Select>
              );
            })()}
          </Stack>
        </Box>

        {/* Loadout fields row.
            Mobile: tight grid with no labels (the dropdowns are self-evident).
            Desktop: spaced row with labels + loadout text + Execute Fire on the right. */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: isMobile
              ? (fuzeType === "TIME" && (projectile || "").toUpperCase() !== "SMOKE"
                  ? "1fr 1fr 60px 56px"
                  : "1fr 1fr 56px")
              : "auto auto auto auto auto 1fr auto",
            gap: isMobile ? 0.6 : 1.25,
            alignItems: isMobile ? "stretch" : "center",
          }}
        >
          {/* Projectile */}
          <Field label="Projectile" hideLabel={isMobile} minWidth={isMobile ? 0 : 92}>
            <Select
              value={projectile || "HE"}
              onChange={(e) => onProjectileChange && onProjectileChange(e.target.value)}
              size="small"
              disabled={!hasMission}
              sx={{
                height: 36,
                borderRadius: 1,
                fontWeight: 600,
                fontSize: 13,
                "& .MuiSelect-select": { py: 0.5, px: 1 },
              }}
            >
              {(projectileOptions && projectileOptions.length ? projectileOptions : PROJECTILE_OPTIONS).map((p) => (
                <MenuItem key={p.value} value={p.value} sx={{ fontWeight: 700, fontSize: 13.5 }}>
                  {p.label}
                </MenuItem>
              ))}
            </Select>
          </Field>

          {/* Fuze */}
          <Field label="Fuze" hideLabel={isMobile} minWidth={isMobile ? 0 : 92}>
            <Tooltip
              arrow
              placement="top"
              title={
                PROJECTILES_REQUIRING_TIME.has((projectile || "").toUpperCase())
                  ? `${projectile} projectile must be fired with Time fuze`
                  : ""
              }
            >
              <span style={{ display: "block" }}>
                <Select
                  value={fuzeType}
                  onChange={(e) => onFuzeTypeChange && onFuzeTypeChange(e.target.value)}
                  size="small"
                  fullWidth
                  disabled={!hasMission || PROJECTILES_REQUIRING_TIME.has((projectile || "").toUpperCase())}
                  sx={{
                    height: 36,
                    borderRadius: 1,
                    fontWeight: 600,
                    fontSize: 13,
                    minWidth: isMobile ? 0 : 92,
                    "& .MuiSelect-select": { py: 0.5, px: 1 },
                  }}
                >
                  {FUZE_OPTIONS.map((f) => (
                    <MenuItem key={f.value} value={f.value} sx={{ fontWeight: 700, fontSize: 13.5 }}>
                      {f.label}
                    </MenuItem>
                  ))}
                </Select>
              </span>
            </Tooltip>
          </Field>

          {/* HoB - only when TIME and NOT smoke (smoke uses fixed table−2 ≈ 100 m HoB). */}
          {fuzeType === "TIME" && (projectile || "").toUpperCase() !== "SMOKE" && (() => {
            const step = 10;
            const label = isMobile ? "HoB" : `HoB (m, ±${step})`;
            return (
              <Field label={label} hideLabel={isMobile} minWidth={isMobile ? 0 : 84}>
                <TextField
                  size="small"
                  type="number"
                  value={heightOfBurst}
                  onChange={(e) => onHeightOfBurstChange && onHeightOfBurstChange(Number(e.target.value))}
                  inputProps={{ min: 0, step }}
                  disabled={!hasMission}
                  placeholder={isMobile ? "HoB" : undefined}
                  sx={{
                    "& .MuiOutlinedInput-root": { height: 36, borderRadius: 1 },
                    "& input": {
                      fontVariantNumeric: "tabular-nums",
                      fontWeight: 600,
                      fontSize: 13,
                      py: 0.5,
                      px: 0.75,
                      textAlign: "center",
                    },
                  }}
                />
              </Field>
            );
          })()}

          {/* Loadout — desktop only. On mobile this is redundant with the Mission panel. */}
          {!isMobile && (
            <Field label="Loadout" minWidth={120}>
              <Typography
                sx={{
                  fontWeight: 600,
                  fontSize: 13.5,
                  lineHeight: "36px",
                  color: hasMission ? "text.primary" : "text.disabled",
                }}
              >
                {loadoutText}
              </Typography>
            </Field>
          )}

          {/* Rounds / gun — dropdown 1..20. Replaces the number input which was awkward
              on mobile (couldn't easily clear the default 1). */}
          <Field label="Rounds / gun" hideLabel={isMobile} minWidth={isMobile ? 0 : 84}>
            <Select
              value={ROUNDS_OPTIONS.includes(Number(rounds)) ? Number(rounds) : 1}
              onChange={(e) => onRoundsChange && onRoundsChange(Number(e.target.value))}
              size="small"
              disabled={!hasMission}
              MenuProps={{
                PaperProps: { sx: { maxHeight: 320 } },
              }}
              sx={{
                height: 36,
                borderRadius: 1,
                fontWeight: 600,
                fontSize: 13,
                "& .MuiSelect-select": {
                  py: 0.5,
                  px: 1,
                  textAlign: "center",
                  fontVariantNumeric: "tabular-nums",
                },
              }}
            >
              {ROUNDS_OPTIONS.map((n) => (
                <MenuItem
                  key={n}
                  value={n}
                  sx={{
                    fontWeight: 700,
                    fontSize: 13.5,
                    fontVariantNumeric: "tabular-nums",
                    justifyContent: "center",
                  }}
                >
                  {n}
                </MenuItem>
              ))}
            </Select>
          </Field>

          {/* Desktop: spacer + Execute Fire to the right */}
          {!isMobile && <Box sx={{ flex: 1, minWidth: 0 }} />}
          {!isMobile && (
            <Tooltip title={tooltip} arrow placement="top">
              <span style={{ display: "inline-flex" }}>
                <Button
                  onClick={(e) => {
                    if (!canFire) return;
                    onExecuteFire(e);
                  }}
                  disabled={!canFire}
                  startIcon={<LocalFireDepartmentRoundedIcon />}
                  sx={(t) => ({
                    minHeight: 44,
                    minWidth: 180,
                    px: 2.5,
                    borderRadius: 1.25,
                    fontWeight: 700,
                    fontSize: 14,
                    letterSpacing: 0.5,
                    textTransform: "uppercase",
                    color: canFire ? (t.palette.mode === "dark" ? "#11150f" : "#f7f7f2") : t.palette.text.disabled,
                    bgcolor: canFire ? (t.palette.mode === "dark" ? "#a6b078" : "#556b2f") : alpha(t.palette.action.disabled, 0.12),
                    border: "1px solid",
                    borderColor: canFire ? (t.palette.mode === "dark" ? "#a6b078" : "#556b2f") : alpha(t.palette.divider, 0.95),
                    boxShadow: canFire ? `0 3px 10px ${alpha(t.palette.mode === "dark" ? "#a6b078" : "#556b2f", 0.3)}` : "none",
                    "&:hover": { bgcolor: canFire ? (t.palette.mode === "dark" ? "#b7c58a" : "#465a27") : undefined },
                    "&.Mui-disabled": { color: t.palette.text.disabled, bgcolor: alpha(t.palette.action.disabled, 0.12) },
                  })}
                >
                  {adjustMode ? "Apply & Fire" : "Execute Fire"}
                </Button>
              </span>
            </Tooltip>
          )}
        </Box>

        {/* Gun chips row (no "On mission" big chip on mobile — covered by the dot indicator on each chip) */}
        {hasMission && batteryOptions.length > 0 && (
          <Box
            sx={(t) => ({
              display: "flex",
              alignItems: "center",
              gap: 0.75,
              flexWrap: "wrap",
              pt: 0.5,
              borderTop: "1px dashed",
              borderTopColor: alpha(t.palette.divider, 0.7),
            })}
          >
            {!isMobile && (
              <Stack direction="row" spacing={0.6} alignItems="center" sx={{ mr: 0.25 }}>
                <Typography sx={{ ...microLabelSx, color: "text.secondary" }}>Guns</Typography>
                <Tooltip arrow title="Guns currently on this mission" placement="top">
                  <Box
                    sx={(t) => ({
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 0.4,
                      px: 0.7,
                      py: 0.15,
                      borderRadius: 0.75,
                      border: "1px solid",
                      borderColor: alpha(t.palette.success.main, 0.45),
                      bgcolor: alpha(t.palette.success.main, t.palette.mode === "dark" ? 0.12 : 0.07),
                      color: t.palette.success.main,
                      fontSize: 10.5,
                      fontWeight: 600,
                      letterSpacing: 0.4,
                    })}
                  >
                    <Box sx={(t) => ({ width: 6, height: 6, borderRadius: "50%", bgcolor: t.palette.success.main })} />
                    <span>
                      {committedGunNumbers.length
                        ? `On mission: ${committedGunNumbers.slice().sort((a, b) => Number(a) - Number(b)).join(", ")}`
                        : "No guns on mission"}
                    </span>
                  </Box>
                </Tooltip>
              </Stack>
            )}

            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
              {batteryOptions.map((gun) => (
                <GunChip
                  key={gun.id}
                  gun={gun}
                  regulatingGunId={regulatingGunId}
                  onToggle={(id) => toggleGunForMission && toggleGunForMission(id)}
                  onFocus={(id) => onFocusGun && onFocusGun(id)}
                />
              ))}
            </Stack>

            <Box sx={{ flex: 1, minWidth: 0 }} />

            {needsSync && (
              <Button
                variant="outlined"
                size="small"
                onClick={handleAssignToMission}
                disabled={assigningGuns}
                sx={(t) => ({
                  minHeight: 34,
                  borderRadius: 1,
                  fontWeight: 600,
                  fontSize: 11.5,
                  letterSpacing: 0.3,
                  textTransform: "none",
                  borderColor: alpha(t.palette.warning.main, 0.5),
                  color: t.palette.warning.main,
                  "&:hover": {
                    borderColor: t.palette.warning.main,
                    bgcolor: alpha(t.palette.warning.main, t.palette.mode === "dark" ? 0.12 : 0.06),
                  },
                })}
              >
                {assigningGuns
                  ? "Adding…"
                  : isMobile
                    ? `+ ${pendingNumbers.join(",")}`
                    : `Add ${pendingNumbers.length === 1 ? "gun" : `${pendingNumbers.length} guns`} ${pendingNumbers.join(", ")} to mission`}
              </Button>
            )}
          </Box>
        )}

        {/* Mobile: Execute Fire as a full-width row at the bottom */}
        {isMobile && (
          <Tooltip title={tooltip} arrow placement="top">
            <span style={{ display: "block" }}>
              <Button
                fullWidth
                onClick={(e) => {
                  if (!canFire) return;
                  onExecuteFire(e);
                }}
                disabled={!canFire}
                startIcon={<LocalFireDepartmentRoundedIcon />}
                sx={(t) => ({
                  minHeight: 44,
                  borderRadius: 1.25,
                  fontWeight: 700,
                  fontSize: 14,
                  letterSpacing: 0.5,
                  textTransform: "uppercase",
                  color: canFire ? (t.palette.mode === "dark" ? "#11150f" : "#f7f7f2") : t.palette.text.disabled,
                  bgcolor: canFire ? (t.palette.mode === "dark" ? "#a6b078" : "#556b2f") : alpha(t.palette.action.disabled, 0.12),
                  border: "1px solid",
                  borderColor: canFire ? (t.palette.mode === "dark" ? "#a6b078" : "#556b2f") : alpha(t.palette.divider, 0.95),
                  boxShadow: canFire ? `0 3px 10px ${alpha(t.palette.mode === "dark" ? "#a6b078" : "#556b2f", 0.3)}` : "none",
                  "&:hover": { bgcolor: canFire ? (t.palette.mode === "dark" ? "#b7c58a" : "#465a27") : undefined },
                  "&.Mui-disabled": { color: t.palette.text.disabled, bgcolor: alpha(t.palette.action.disabled, 0.12) },
                })}
              >
                {adjustMode ? "Apply & Fire" : "Fire"}
              </Button>
            </span>
          </Tooltip>
        )}
      </Stack>
    </Box>
  );
}
